import * as d3 from 'd3';
import { defineTexturedGlassPatterns } from './../glassPatternsDefs';

// Function to draw trapezoids with frames (similar to shapeTriangle but for trapezoids)
// For trapezoid: bottom side has width w, top side has width X (default w/2)
// Left side has height h, right side has height Y (default h/2)
// orientation 'left': left side is perpendicular (vertical), diagonal on right
// orientation 'right': right side is perpendicular (vertical), diagonal on left
export function shapeTrapezoid(svg, d, x, y, trapezoids, labelTextOrOptions = null, subFrame = false, fullCorners = false, halfCorners = false,
  displayLabel = true, labelOptions = {}, drawFrame = true) {

  // Support both old positional style and new object style
  let labelText, options;
  if (typeof labelTextOrOptions === 'object' && labelTextOrOptions !== null && !Array.isArray(labelTextOrOptions)) {
    // New style: options object passed
    options = labelTextOrOptions;
    labelText = options.labelText ?? null;
    subFrame = options.subFrame ?? false;
    fullCorners = options.fullCorners ?? false;
    halfCorners = options.halfCorners ?? false;
    displayLabel = options.displayLabel ?? true;
    labelOptions = options.labelOptions ?? {};
    drawFrame = options.drawFrame ?? true;
  } else {
    // Old style: positional parameters
    labelText = labelTextOrOptions;
  }

  let currentX = x;
  let prev = {};
  let innerPoints, outerPoints;

  const {
    fontSize = 14,
    fontColor = 'black',
    fontFamily = 'Arial',
    fontOpacity = 0.4,
  } = labelOptions;

  trapezoids.forEach((trapezoid, index) => {
    const {
      w, // bottom width
      h = prev.h ?? 100, // left side height
      X = prev.X ?? w / 2, // top width (default: half of bottom width)
      Y = prev.Y ?? h / 2, // right side height (default: half of left height)
      color = prev.color ?? 'white',
      glassType = prev.glassType || null,
      glassDesign = prev.glassDesign || null,
      frameWidth = prev.frameWidth ?? 0,
      frameColor = prev.frameColor ?? 'white',
      lineFrameColor = prev.lineFrameColor ?? frameColor,
      lineFrameThickness = prev.lineFrameThickness ?? 1,
      outerStrokeColor = prev.outerStrokeColor ?? 'black',
      outerStrokeWidth = prev.outerStrokeWidth ?? 1,
      orientation = prev.orientation ?? 'right', // 'left' or 'right'
      forceSubFrame = prev.forceSubFrame || false,
      fullCorners = prev.fullCorners || false,
      halfCorners = prev.halfCorners || false,
      displayLabel = prev.displayLabel ?? true,
      middleFrame = prev.middleFrame ?? true  // Default true to show middle frame
    } = trapezoid;

    let baseFill = color;
    let tintOverlay = null;

    // TINTED GLASS
    if (glassType === 'Tinted') {
      baseFill = 'white';
      switch (glassDesign?.toLowerCase()) {
        case 'grey':
          tintOverlay = 'rgba(60, 60, 60, 0.2)';
          break;
        case 'bronze':
          tintOverlay = 'rgba(90, 50, 0, 0.2)';
          break;
        default:
          console.warn('Unknown tinted glass design:', glassDesign);
      }
    }

    // TEXTURED GLASS
    if (glassType === 'Textured' && glassDesign) {
      const patternId = `${glassDesign.toLowerCase().replace(/\s/g, '')}Pattern`;
      tintOverlay = `url(#${patternId})`;
      defineTexturedGlassPatterns(svg, glassDesign);
    }

    // Define trapezoid points based on orientation
    if (orientation === 'right') {
      // Right trapezoid: left side is perpendicular (vertical), diagonal on right
      // The trapezoid touches the outer rectangle:
      // - Bottom spans full width w at y+h
      // - Left side is full height h and perpendicular
      // - Top is parallel to bottom at y, width (w - X), aligned to left
      // - Right side is diagonal connecting bottom-right to top-right at (w-X, y)
      //
      // A = bottom-left corner
      // B = top-left corner (at left edge, at y position)
      // C = top-right corner (at w-X from currentX, at y position)
      // D = bottom-right corner

      const A = [currentX - frameWidth, y + h + frameWidth];               // bottom-left
      const B = [currentX - frameWidth, y - frameWidth];                   // top-left (vertical left side, at y)
      const C = [currentX + (w - X) + frameWidth, y - frameWidth];         // top-right (w-X from left, at y)
      const D = [currentX + w + frameWidth, y + h + frameWidth];           // bottom-right

      outerPoints = [A, B, C, D];

      function offsetLine(P, Q, d) {
        const dx = Q[0] - P[0];
        const dy = Q[1] - P[1];
        const len = Math.hypot(dx, dy);

        // inward normal
        const nx = -dy / len;
        const ny = dx / len;

        return [
          [P[0] + nx * d, P[1] + ny * d],
          [Q[0] + nx * d, Q[1] + ny * d]
        ];
      }

      function intersect(L1, L2) {
        const [A, B] = L1;
        const [C, D] = L2;

        const a1 = B[1] - A[1];
        const b1 = A[0] - B[0];
        const c1 = a1 * A[0] + b1 * A[1];

        const a2 = D[1] - C[1];
        const b2 = C[0] - D[0];
        const c2 = a2 * C[0] + b2 * C[1];

        const det = a1 * b2 - a2 * b1;

        return [
          (b2 * c1 - b1 * c2) / det,
          (a1 * c2 - a2 * c1) / det
        ];
      }

      // Offset each edge inward by frameWidth
      const AB = offsetLine(A, B, frameWidth);
      const BC = offsetLine(B, C, frameWidth);
      const CD = offsetLine(C, D, frameWidth);
      const DA = offsetLine(D, A, frameWidth);

      const A2 = intersect(AB, DA);
      const B2 = intersect(AB, BC);
      const C2 = intersect(BC, CD);
      const D2 = intersect(CD, DA);

      innerPoints = [A2, B2, C2, D2];
    }
    else { // orientation === 'left'
      // Left trapezoid: right side is perpendicular (vertical), diagonal on left
      // The trapezoid touches the outer rectangle:
      // - Bottom spans full width w at y+h
      // - Right side is full height h and perpendicular
      // - Top is parallel to bottom at y, positioned X units from left
      // - Left side is diagonal connecting bottom-left to top point at (X, y)
      // - Y parameter controls the height difference (top is at y + (h-Y))
      //
      // Outer trapezoid (with frame extension):
      // A = bottom-left corner
      // B = top-left corner (at X offset from left, at top y position)
      // C = top-right corner
      // D = bottom-right corner

      const A = [currentX - frameWidth, y + h + frameWidth];           // bottom-left
      const B = [currentX + X - frameWidth, y - frameWidth];           // top-left (X from left, at y position)
      const C = [currentX + w + frameWidth, y - frameWidth];           // top-right (same Y as B)
      const D = [currentX + w + frameWidth, y + h + frameWidth];       // bottom-right

      outerPoints = [A, B, C, D];

      // Helper: offset a line segment inward by distance d
      function offsetLine(P, Q, d) {
        const dx = Q[0] - P[0];
        const dy = Q[1] - P[1];
        const len = Math.hypot(dx, dy);

        // inward normal (perpendicular to the left)
        const nx = -dy / len;
        const ny = dx / len;

        return [
          [P[0] + nx * d, P[1] + ny * d],
          [Q[0] + nx * d, Q[1] + ny * d]
        ];
      }

      // Helper: intersection of two infinite lines
      function intersect(L1, L2) {
        const [A, B] = L1;
        const [C, D] = L2;

        const a1 = B[1] - A[1];
        const b1 = A[0] - B[0];
        const c1 = a1 * A[0] + b1 * A[1];

        const a2 = D[1] - C[1];
        const b2 = C[0] - D[0];
        const c2 = a2 * C[0] + b2 * C[1];

        const det = a1 * b2 - a2 * b1;

        return [
          (b2 * c1 - b1 * c2) / det,
          (a1 * c2 - a2 * c1) / det
        ];
      }

      // Offset each edge inward by frameWidth
      const AB = offsetLine(A, B, frameWidth);
      const BC = offsetLine(B, C, frameWidth);
      const CD = offsetLine(C, D, frameWidth);
      const DA = offsetLine(D, A, frameWidth);

      // Intersect offset edges to get inner trapezoid
      const A2 = intersect(AB, DA);
      const B2 = intersect(AB, BC);
      const C2 = intersect(BC, CD);
      const D2 = intersect(CD, DA);

      innerPoints = [A2, B2, C2, D2];
    }

    // === OUTER FRAME ===
    if (drawFrame && frameWidth > 0) {
      const outerPointsStr = outerPoints.map(p => p.join(',')).join(' ');
      svg.append('polygon')
        .attr('points', outerPointsStr)
        .attr('fill', frameColor)
        .attr('stroke', lineFrameColor)
        .attr('stroke-width', lineFrameThickness);
    }

    // === BASE FILL ===
    const innerPointsStr = innerPoints.map(p => p.join(',')).join(' ');
    svg.append('polygon')
      .attr('points', innerPointsStr)
      .attr('fill', baseFill)
      .attr('stroke', outerStrokeColor)
      .attr('stroke-width', outerStrokeWidth);

    // === TINT OVERLAY ===
    if (tintOverlay) {
      svg.append('polygon')
        .attr('points', innerPointsStr)
        .attr('fill', tintOverlay)
        .attr('stroke', 'none');
    }

    // === SUBFRAME (middle frame between outer frame and glass) ===
    if (drawFrame && ((subFrame === true && index === 0) || (forceSubFrame === true)) && middleFrame) {
      const subInset = frameWidth / 2; // distance from outer frame to middle frame

      // Calculate middle frame trapezoid points by offsetting from outer points
      let middlePoints;

      if (orientation === 'right') {
        const [A, B, C, D] = outerPoints;

        function offsetLine(P, Q, d) {
          const dx = Q[0] - P[0];
          const dy = Q[1] - P[1];
          const len = Math.hypot(dx, dy);
          const nx = -dy / len;
          const ny = dx / len;
          return [
            [P[0] + nx * d, P[1] + ny * d],
            [Q[0] + nx * d, Q[1] + ny * d]
          ];
        }

        function intersect(L1, L2) {
          const [A, B] = L1;
          const [C, D] = L2;
          const a1 = B[1] - A[1];
          const b1 = A[0] - B[0];
          const c1 = a1 * A[0] + b1 * A[1];
          const a2 = D[1] - C[1];
          const b2 = C[0] - D[0];
          const c2 = a2 * C[0] + b2 * C[1];
          const det = a1 * b2 - a2 * b1;
          return [
            (b2 * c1 - b1 * c2) / det,
            (a1 * c2 - a2 * c1) / det
          ];
        }

        const AB = offsetLine(A, B, subInset);
        const BC = offsetLine(B, C, subInset);
        const CD = offsetLine(C, D, subInset);
        const DA = offsetLine(D, A, subInset);

        const A2 = intersect(AB, DA);
        const B2 = intersect(AB, BC);
        const C2 = intersect(BC, CD);
        const D2 = intersect(CD, DA);

        middlePoints = [A2, B2, C2, D2];
      } else { // left
        const [A, B, C, D] = outerPoints;

        function offsetLine(P, Q, d) {
          const dx = Q[0] - P[0];
          const dy = Q[1] - P[1];
          const len = Math.hypot(dx, dy);
          const nx = -dy / len;
          const ny = dx / len;
          return [
            [P[0] + nx * d, P[1] + ny * d],
            [Q[0] + nx * d, Q[1] + ny * d]
          ];
        }

        function intersect(L1, L2) {
          const [A, B] = L1;
          const [C, D] = L2;
          const a1 = B[1] - A[1];
          const b1 = A[0] - B[0];
          const c1 = a1 * A[0] + b1 * A[1];
          const a2 = D[1] - C[1];
          const b2 = C[0] - D[0];
          const c2 = a2 * C[0] + b2 * C[1];
          const det = a1 * b2 - a2 * b1;
          return [
            (b2 * c1 - b1 * c2) / det,
            (a1 * c2 - a2 * c1) / det
          ];
        }

        const AB = offsetLine(A, B, subInset);
        const BC = offsetLine(B, C, subInset);
        const CD = offsetLine(C, D, subInset);
        const DA = offsetLine(D, A, subInset);

        const A2 = intersect(AB, DA);
        const B2 = intersect(AB, BC);
        const C2 = intersect(BC, CD);
        const D2 = intersect(CD, DA);

        middlePoints = [A2, B2, C2, D2];
      }

      const middlePointsStr = middlePoints.map(p => p.join(',')).join(' ');
      svg.append('polygon')
        .attr('points', middlePointsStr)
        .attr('fill', 'none')
        .attr('stroke', 'black')
        .attr('stroke-width', 0.5);
    }

    // === LABEL ===
    const textToUse = labelText == null ? index + 1 : labelText;

    if (displayLabel) {
      // Position label at centroid of trapezoid
      // Centroid of trapezoid: weighted average of top and bottom midpoints
      // For a trapezoid with parallel sides of lengths a (top) and b (bottom) and height h:
      // centroidY = h * (2*b + a) / (3 * (a + b)) from bottom

      let centroidX, centroidY;

      if (orientation === 'right') {
        // Right orientation:
        // Top edge: from (X, y) to (w, y), length = w - X
        // Bottom edge: from (0, y+h) to (w, y+h), length = w
        const topWidth = w - X;
        const bottomWidth = w;
        const topMidX = currentX + X + topWidth / 2;
        const bottomMidX = currentX + bottomWidth / 2;
        centroidX = (topMidX * topWidth + bottomMidX * bottomWidth) / (topWidth + bottomWidth);

        const trapHeight = h;
        centroidY = y + trapHeight * (2 * topWidth + bottomWidth) / (3 * (topWidth + bottomWidth));
      } else {
        // Left orientation:
        // Top edge: from (0, y) to (w-X, y), length = w - X
        // Bottom edge: from (0, y+h) to (w, y+h), length = w
        const topWidth = w - X;
        const bottomWidth = w;
        const topMidX = currentX + topWidth / 2;
        const bottomMidX = currentX + bottomWidth / 2;
        centroidX = (topMidX * topWidth + bottomMidX * bottomWidth) / (topWidth + bottomWidth);

        const trapHeight = h;
        centroidY = y + trapHeight * (2 * topWidth + bottomWidth) / (3 * (topWidth + bottomWidth));
      }

      svg.append('text')
        .attr('x', centroidX)
        .attr('y', centroidY + fontSize / 3)
        .attr('text-anchor', 'middle')
        .attr('dominant-baseline', 'middle')
        .attr('font-size', fontSize)
        .attr('fill', fontColor)
        .attr('fill-opacity', fontOpacity)
        .attr('font-family', fontFamily)
        .text(textToUse);
    }

    currentX += w + 2 * frameWidth + d + 2 * lineFrameThickness;

    prev = {
      w, h, X, Y, color,
      frameWidth, frameColor,
      lineFrameColor, lineFrameThickness,
      outerStrokeColor, outerStrokeWidth,
      orientation, fullCorners, halfCorners, displayLabel,
      glassType, glassDesign, middleFrame
    };
  });
  return innerPoints;
}
